import { useState } from 'react';
import { useReveal } from '../hooks/useReveal';

interface Step {
  num: string;
  icon: string;
  title: string;
  desc: string;
}

const TAB_DATA: Record<string, Step[]> = {
  patients: [
    { num: 'Step 01', icon: '\uD83D\uDC64', title: 'Create Your Profile', desc: 'Register with your Ghana Card, verify your identity, and add your NHIS card for seamless insurance billing.' },
    { num: 'Step 02', icon: '\uD83D\uDD0D', title: 'Find a Clinician', desc: 'Search doctors, nurses, and physician assistants by specialty, location, availability, and NHIS acceptance.' },
    { num: 'Step 03', icon: '\uD83D\uDCC5', title: 'Book & Pay', desc: 'Choose virtual or in-person. Pay with MTN MoMo, Vodafone Cash, AirtelTigo, or use your NHIS coverage.' },
    { num: 'Step 04', icon: '\uD83D\uDC8A', title: 'Manage Your Health', desc: 'Track vitals, log medications, set reminders, view lab results, and order pharmacy deliveries \u2014 all in one place.' },
  ],
  clinicians: [
    { num: 'Step 01', icon: '\uD83C\uDF93', title: 'Verify Credentials', desc: 'Submit your MDC/NMC/PCGH license number. Our system verifies with regulatory bodies automatically.' },
    { num: 'Step 02', icon: '\uD83D\uDDD3', title: 'Set Your Schedule', desc: 'Configure your availability by day, time, visit type (virtual/in-person/home), and slot duration. Full flexibility.' },
    { num: 'Step 03', icon: '\uD83E\uDE7A', title: 'See Patients', desc: 'Video consultations with integrated patient records, AI-powered SOAP notes, and real-time clinical decision support.' },
    { num: 'Step 04', icon: '\uD83D\uDCB0', title: 'Get Paid Seamlessly', desc: 'Automatic Hubtel billing, NHIS claims submission, and transparent revenue dashboards. No paperwork.' },
  ],
  facilities: [
    { num: 'Step 01', icon: '\uD83C\uDFE5', title: 'Register Your Facility', desc: 'Submit GHS registration and NHIS accreditation details. Admin verification within 48 hours.' },
    { num: 'Step 02', icon: '\uD83D\uDC65', title: 'Build Your Team', desc: 'Invite doctors, nurses, receptionists, and lab staff. Assign roles and manage permissions centrally.' },
    { num: 'Step 03', icon: '\uD83D\uDCCA', title: 'Manage Operations', desc: 'Full appointment management, patient flow, billing, inventory, and NHIS claims from one dashboard.' },
    { num: 'Step 04', icon: '\uD83D\uDCC8', title: 'Grow with Analytics', desc: 'Real-time revenue analytics, patient demographics, appointment patterns, and NHIS claim recovery rates.' },
  ],
  pharmacies: [
    { num: 'Step 01', icon: '\uD83C\uDFEA', title: 'List Your Pharmacy', desc: 'Register with PCGH license, set your location, operating hours, and upload your inventory.' },
    { num: 'Step 02', icon: '\uD83D\uDCE6', title: 'Manage Inventory', desc: 'Real-time stock tracking, automatic low-stock alerts, expiry date monitoring, and NHIS price management.' },
    { num: 'Step 03', icon: '\u2705', title: 'Verify Prescriptions', desc: 'Scan QR codes from digital prescriptions. AI drug interaction check before dispensing. One-click NHIS claim.' },
    { num: 'Step 04', icon: '\uD83D\uDEF5', title: 'Deliver & Earn', desc: 'Offer delivery services. Track riders in real time. Patients pay via MoMo and you get same-day settlements.' },
  ],
};

const TAB_LABELS: { key: string; label: string }[] = [
  { key: 'patients', label: 'Patients' },
  { key: 'clinicians', label: 'Clinicians' },
  { key: 'facilities', label: 'Facilities' },
  { key: 'pharmacies', label: 'Pharmacies' },
];

export default function HowItWorks() {
  const [activeTab, setActiveTab] = useState('patients');
  const headerRef = useReveal();

  return (
    <section className="section" id="features">
      <div className="section-header reveal" ref={headerRef}>
        <div className="section-tag">How It Works</div>
        <h2 className="section-title">Built for everyone in the healthcare journey</h2>
        <p className="section-subtitle">
          Whether you&apos;re a patient, a clinician, or managing a facility, Heka.care is
          designed for you.
        </p>
      </div>

      <div className="tabs">
        {TAB_LABELS.map(({ key, label }) => (
          <button
            key={key}
            className={`tab-btn${activeTab === key ? ' active' : ''}`}
            onClick={() => setActiveTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {TAB_LABELS.map(({ key }) => (
        <div key={key} className={`tab-content${activeTab === key ? ' active' : ''}`}>
          <div className="steps-grid">
            {TAB_DATA[key].map((step) => (
              <div className="step-card" key={step.title}>
                <div className="step-num">{step.num}</div>
                <div className="step-icon">{step.icon}</div>
                <div className="step-title">{step.title}</div>
                <div className="step-desc">{step.desc}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}
